import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { multiplyRupees, sumRupees } from './inr';

const ORGANIZATIONS = {
  COG: { name: 'COG India Art Foundation', address: 'P384 Keyatala Lane, Golpark, Kolkata 700029', hours: '12pm - 8pm, Tuesday closed', phone: '+91 7595957063' },
  'The Bengal Craft': { name: 'The Bengal Craft', address: 'P384 Keyatala Lane, Golpark, Kolkata 700029', hours: '12pm - 8pm, Tuesday closed', phone: '+91 7595957063' }
};
const known = value => value !== null && value !== undefined && String(value).trim() !== '' && Number.isFinite(Number(value));
const money = value => known(value) ? Number(value).toLocaleString('en-IN', { maximumFractionDigits: 6 }) : '-';
const date = value => value && Number.isFinite(new Date(value).getTime()) ? new Date(value).toLocaleDateString('en-IN') : '-';
const addressText = value => typeof value === 'string' ? value : value && typeof value === 'object' ? ['line1', 'line2', 'city', 'state', 'postalCode', 'pincode', 'country'].map(key => value[key]).filter(Boolean).join(', ') : '';

export function createInvoiceReceipt(saleDoc) {
  const org = ORGANIZATIONS[saleDoc.organization] || { name: saleDoc.organization || 'Organization not recorded' };
  const pdf = new jsPDF({ unit: 'pt', format: 'a4' });
  const pad = 48; const pageW = pdf.internal.pageSize.getWidth(); const pageH = pdf.internal.pageSize.getHeight();
  const contentW = pageW - 2 * pad; const bottom = pageH - 60;
  let y = 48;
  function ensure(height) { if (y + height > bottom) { pdf.addPage(); y = 56; } }
  function text(value, size = 10, bold = false, height = 14) {
    pdf.setFont('helvetica', bold ? 'bold' : 'normal'); pdf.setFontSize(size);
    for (const paragraph of String(value).split(/\r?\n/)) {
      const lines = pdf.splitTextToSize(paragraph || ' ', contentW);
      for (const line of lines) { ensure(height); pdf.text(line || ' ', pad, y); y += height; }
    }
  }
  text(`${org.name} - Receipt`, 16, true, 20);
  const orgAddress = addressText(saleDoc.organizationAddress) || org.address;
  if (orgAddress) text(orgAddress, 9, false, 12);
  if (saleDoc.organizationPhone || org.phone) text(`Phone: ${saleDoc.organizationPhone || org.phone}`, 9, false, 12);
  if (org.hours) text(`Hours: ${org.hours}`, 9, false, 12);
  y += 8;
  text(`Invoice: ${saleDoc.invoiceNumber || saleDoc._id || '-'}`);
  text(`Invoice Date: ${date(saleDoc.invoiceDate || saleDoc.date)} | Mode: ${saleDoc.paymentMode || '-'}`);
  text(`Customer: ${saleDoc.customerName || '-'}`);
  if (saleDoc.whatsapp) text(`WhatsApp: ${saleDoc.whatsapp}`);
  if (saleDoc.customerPhone) text(`Customer phone: ${saleDoc.customerPhone}`);
  const customerAddress = addressText(saleDoc.customerAddress || saleDoc.address);
  if (customerAddress) text(`Customer address: ${customerAddress}`);
  y += 10;
  const items = Array.isArray(saleDoc.items) ? saleDoc.items : [];
  const lineAmount = item => known(item?.amount) && Number.isSafeInteger(item?.qty) && item.qty >= 0 ? multiplyRupees(Number(item.amount), item.qty, true) : null;
  const table = (head, body) => {
    ensure(50);
    autoTable(pdf, { head, body, rowPageBreak: 'avoid', startY: y, margin: { left: pad, right: pad, top: 56, bottom: 60 },
      styles: { font: 'helvetica', fontSize: 10, cellPadding: 6, textColor: 0, lineColor: 0, overflow: 'linebreak' },
      headStyles: { fillColor: [255, 255, 255], textColor: 0, lineWidth: 0.5, lineColor: 0 },
      columnStyles: { [head[0].length - 1]: { halign: 'right' } }, theme: 'grid' });
    y = (pdf.lastAutoTable?.finalY || y) + 22;
  };
  table([['Item', 'Qty', 'Unit price (INR)', 'Amount (INR)']], items.map(item => [item?.label || '-', item?.qty ?? '-', money(item?.amount), money(lineAmount(item))]));
  const lines = items.map(lineAmount); const gross = lines.every(value => value !== null) ? sumRupees(lines, true) : null;
  ensure(95); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(10);
  for (const [label, value] of [['Gross', gross], ['Discount', saleDoc.discount], ['Total', saleDoc.totalAmount], ['Received', saleDoc.amountReceived], ['Due', saleDoc.balanceDue]]) {
    pdf.text(`${label} (INR): ${money(value)}`, pageW - pad, y, { align: 'right' }); y += 16;
  }
  y += 14; ensure(65); text('Payment History', 11, true); y += 4;
  const payments = Array.isArray(saleDoc.payments) ? saleDoc.payments : [];
  if (payments.length) table([['Date', 'Mode', 'Amount (INR)']], payments.map(payment => [date(payment.date), payment.mode || '-', money(payment.amount)]));
  else text('No recorded payment transactions.', 10);
  if (saleDoc.notes && String(saleDoc.notes).trim()) { y += 10; text(`Notes: ${saleDoc.notes}`); y += 8; }
  if (known(saleDoc.balanceDue) && Number(saleDoc.balanceDue) === 0) {
    ensure(36); pdf.setDrawColor(0); pdf.roundedRect(pageW - pad - 120, y, 120, 28, 6, 6, 'S'); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(11); pdf.text('NO DUE', pageW - pad - 60, y + 18, { align: 'center' });
  }
  const pages = pdf.internal.getNumberOfPages();
  for (let page = 1; page <= pages; page++) { pdf.setPage(page); pdf.setFont('helvetica', 'normal'); pdf.setFontSize(9); pdf.text('Running Sales - Invoice Receipt', pad, pageH - 28); pdf.text(`${page} / ${pages}`, pageW - pad, pageH - 28, { align: 'right' }); }
  return pdf;
}
export function downloadInvoiceReceipt(saleDoc) {
  const pdf = createInvoiceReceipt(saleDoc);
  pdf.save(`Invoice_${String(saleDoc.invoiceNumber || saleDoc._id || 'receipt').replace(/[^a-zA-Z0-9_-]/g, '_')}.pdf`);
  return pdf;
}
